import { useEffect, useState } from 'react'
import { Box, Button, Grid, Link, List, ListItem, Stack, Typography } from '@mui/material'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import { styled, useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowForward } from '@mui/icons-material'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import { ReactComponent as AverageIcon } from '../../assets/average.svg'
import { ReactComponent as MedianIcon } from '../../assets/median.svg'
import { ReactComponent as WarningIcon } from '../../assets/warning.svg'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { setPtKPIsForCurrentRun, setPtRuns } from '../../store/actions'
import { getIsDemoAccount } from '../../utils'
import FsDemoAccountModal from '../../components/common/molecules/DemoAccountModal'
import { useLoading } from '../../shared/hooks'
import ACTIONS from '../../store/constants'
import { getIcon } from '../../components/common/Icons'
import LoadingDash from '../../components/common/atoms/LoadingDash'

const PtHome = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { currentPtRun, ptKPIs } = useAppSelector((state) => state.currentPt)
  const { currentLanguage, account } = useAppSelector((state) => state.currentUser)
  const { t } = useTranslation()
  const [whichIsOpen, setWhichIsOpen] = useState<string | null>(null)
  const [openDemoModal, setOpenDemoModal] = useState<boolean>(false)

  const isDemoAccount = getIsDemoAccount()

  useEffect(() => {
    dispatch(setPtRuns())
  }, [])

  useEffect(() => {
    if (currentPtRun) {
      if (!ptKPIs?.[currentPtRun.id]) {
        dispatch(setPtKPIsForCurrentRun({ runId: currentPtRun.id }))
      }
    }
  }, [currentPtRun])

  const openDetails = (which: string | null) => {
    if (isDemoAccount) {
      setOpenDemoModal(true)
      return
    }
    setWhichIsOpen(whichIsOpen === which ? null : which)
  }

  const curKPIs: any = ptKPIs && currentPtRun ? ptKPIs[currentPtRun.id] : null
  let cwiAboveFive = 0
  let totalcwi = 0
  if (curKPIs && curKPIs[3] && curKPIs[3].sections[0] && curKPIs[3].sections[0].results) {
    curKPIs[3].sections[0].results.forEach((r: any) => {
      if (r.value && r.value[0] && r.value[0].value > 5) cwiAboveFive++
      totalcwi++
    })
  }

  const { isFetching } = useLoading([
    ACTIONS.PT.SET_PT_RUNS,
    ACTIONS.PT.SET_PT_KPIS_FOR_CURRENT_RUN,
  ])

  const Accordion = styled((aprops: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...aprops} />
  ))(() => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:first-of-type': {
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
    },
    '&:not(:last-of-type)': {
      borderBottom: 0,
    },
    '&:last-of-type': {
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
    },
    '&:before': {
      display: 'none',
    },
  }))

  const AccordionSummary = styled((aprops: AccordionSummaryProps) => (
    <MuiAccordionSummary
      expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '1rem' }} />}
      {...aprops}
    />
  ))(() => ({
    backgroundColor: theme.palette.action.hover,
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
      marginLeft: theme.spacing(1),
    },
  }))

  const AccordionDetails = styled(MuiAccordionDetails)(() => ({
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  }))

  return (
    <>
      <FsDemoAccountModal isOpen={openDemoModal} setIsOpen={setOpenDemoModal} app="pt" />

      <Stack mb={1} direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center">
          {getIcon('pt-home', { fontSize: 'large' })}
          <Typography variant="h1" ml={1} mt={0.5}>
            {t('pt.home.title')}
          </Typography>
        </Stack>
      </Stack>

      <Box
        sx={{
          overflowY: 'auto',
          height: 'calc(100vh - 137px)',
          pb: 6,
          maxWidth: 1000,
          px: 2,
          textAlign: 'justify',
        }}
      >
        <Typography mt={2}>{t('pt.home.desc')}</Typography>
        {isFetching ? (
          <Box sx={{ height: '280px', overflow: 'hidden', pt: 5 }}>
            <LoadingDash title={t('loading')} />
          </Box>
        ) : curKPIs ? (
          <>
            <Typography variant="h2" mt={4} mb={2}>
              {`${t('pt.home.current.kpis')} ${currentPtRun?.year}`}
            </Typography>
            <Grid container gap={2} justifyContent="center">
              <Grid
                size={{ xs: 12, sm: 6, md: 2.5 }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  pt: 3,
                  pb: 2,
                  px: 2,
                  boxShadow: `2px 2px 8px 2px ${theme.palette.primary.main}66`,
                  borderRadius: 2,
                  textAlign: 'center',
                }}
              >
                <AverageIcon width={57} height={49} color={theme.palette.primary.main} />
                <Typography variant="n1" mt={2}>{`${Number(
                  curKPIs[0].sections[0].results[0].value,
                ).toLocaleString(currentLanguage, {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                })} %`}</Typography>
                <Typography mt={2}>{curKPIs[0].sections[0].title.text[currentLanguage]}</Typography>
              </Grid>
              <Grid
                size={{ xs: 12, sm: 6, md: 2.5 }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  pt: 3,
                  pb: 2,
                  px: 2,
                  boxShadow: `1px 1px 5px 3px ${theme.palette.primary.main}30`,
                  borderRadius: 2,
                  textAlign: 'center',
                }}
              >
                <MedianIcon color={theme.palette.primary.main} />
                <Typography variant="n1" mt={2}>{`${Number(
                  curKPIs[0].sections[2].results[0].value,
                ).toLocaleString(currentLanguage, {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                })} %`}</Typography>
                <Typography mt={2}>{curKPIs[0].sections[2].title.text[currentLanguage]}</Typography>
              </Grid>
              {cwiAboveFive !== 0 && (
                <Grid
                  size={{ xs: 12, sm: 6, md: 4 }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    p: 2,
                    boxShadow: `1px 1px 5px 3px ${theme.palette.primary.main}30`,
                    borderRadius: 2,
                    textAlign: 'center',
                  }}
                >
                  <Stack mt={1} alignItems="center">
                    <WarningIcon height={52} color={theme.palette.primary.main} />
                  </Stack>
                  <Typography variant="n1" mt={2}>
                    {cwiAboveFive} / {totalcwi}
                  </Typography>
                  <Typography mt={2}>
                    {currentLanguage === 'de'
                      ? 'Gruppen gleichwertige Arbeit'
                      : 'Group(s) of Work of Equal Value'}
                  </Typography>
                  <Typography variant="body2" className="my-2">
                    {t('pt.home.cwis.warning', { number: cwiAboveFive })}
                  </Typography>
                  {account?.profile.appAccess?.includes('fs') ? (
                    currentLanguage === 'de' ? (
                      <Link onClick={() => navigate('/fs/dashboard')} sx={{ cursor: 'pointer' }}>
                        <Typography> Ergreifen Sie jetzt Maßnahmen!</Typography>
                      </Link>
                    ) : (
                      <Link onClick={() => navigate('/fs/dashboard')} sx={{ cursor: 'pointer' }}>
                        <Typography>Take measures now!</Typography>
                      </Link>
                    )
                  ) : currentLanguage === 'de' ? (
                    <Link target="_blank" href="https://www.ines-analytics.com/fair-solution">
                      <Typography> Ergreifen Sie jetzt Maßnahmen!</Typography>
                    </Link>
                  ) : (
                    <Link target="_blank" href="https://www.ines-analytics.com/en/fair-solution">
                      <Typography>Take measures now!</Typography>
                    </Link>
                  )}
                </Grid>
              )}
              <Grid
                size={{ xs: 12, sm: 6, md: 2 }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 2,
                  border: `1px solid ${theme.palette.secondary.light}`,
                  borderRadius: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: theme.palette.secondary.light,
                  '&:hover': {
                    boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                    transform: 'scale(1.02)',
                  },
                }}
                onClick={() => navigate('/pt/kpis')}
              >
                <ArrowForward fontSize="large" />

                <Typography>{t('pt.home.see.all')}</Typography>
              </Grid>
            </Grid>
          </>
        ) : (
          <Stack
            mt={4}
            sx={{
              border: `1px solid ${theme.palette.primary.main}`,
              borderRadius: 2,
              p: 4,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h3">{t('pt.home.no.kpis')}</Typography>
            <Typography mb={2}>{t('pt.home.upload.prompt')}</Typography>
            <Button variant="contained" onClick={() => navigate('/pt/upload-files')}>
              {t('pt.home.upload')}
            </Button>
          </Stack>
        )}
        <Typography variant="h2" mt={4} mb={2}>
          {t('pt.faq.title')}
        </Typography>
        <Accordion>
          <AccordionSummary aria-controls="panel1-content" id="panel1-header">
            <Typography>{t('pt.faq.q.1')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>{t('pt.faq.a.1')}</Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary aria-controls="panel1-content" id="panel1-header">
            <Typography>{t('pt.faq.q.2')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List sx={{ listStyleType: 'disc' }}>
              <ListItem sx={{ display: 'list-item', ml: 2 }}>
                <Typography>{t('pt.faq.a.2.1')}</Typography>
              </ListItem>
              <ListItem sx={{ display: 'list-item', ml: 2 }}>
                <Typography>{t('pt.faq.a.2.2')}</Typography>
              </ListItem>
              <ListItem sx={{ display: 'list-item', ml: 2 }}>
                <Typography>{t('pt.faq.a.2.3')}</Typography>
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary aria-controls="panel1-content" id="panel1-header">
            <Typography>{t('pt.faq.q.3')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List sx={{ listStyleType: 'disc' }}>
              <ListItem sx={{ display: 'list-item', ml: 2 }}>
                <Typography>{t('pt.faq.a.3.1')}</Typography>
              </ListItem>
              <ListItem sx={{ display: 'list-item', ml: 2 }}>
                <Typography>{t('pt.faq.a.3.2')}</Typography>
              </ListItem>
              <ListItem sx={{ display: 'list-item', ml: 2 }}>
                <Typography>{t('pt.faq.a.3.3')}</Typography>
              </ListItem>
              <ListItem sx={{ display: 'list-item', ml: 2 }}>
                <Typography>{t('pt.faq.a.3.4')}</Typography>
              </ListItem>
              <ListItem sx={{ display: 'list-item', ml: 2 }}>
                <Typography>{t('pt.faq.a.3.5')}</Typography>
              </ListItem>
              <ListItem sx={{ display: 'list-item', ml: 2 }}>
                <Typography>{t('pt.faq.a.3.6')}</Typography>
              </ListItem>
              <ListItem sx={{ display: 'list-item', ml: 2 }}>
                <Typography>{t('pt.faq.a.3.7')}</Typography>
              </ListItem>
            </List>
            {curKPIs && (
              <Stack direction="row" justifyContent="flex-end">
                <Button variant="outlined" onClick={() => navigate('/pt/kpis')} size="small">
                  {t('pt.home.see.all')}
                </Button>
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary aria-controls="panel1-content" id="panel1-header">
            <Typography>{t('pt.faq.q.4')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography fontWeight="semibold">{t('pt.faq.a.4')}</Typography>
            <Typography>
              {t('pt.faq.a.4.1')}{' '}
              <Link target="_blank" href="https://d-nb.info/1272621421/34">
                SOURCE
              </Link>
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary aria-controls="panel1-content" id="panel1-header">
            <Typography>{t('pt.faq.q.5')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List sx={{ listStyleType: 'disc' }}>
              <ListItem sx={{ display: 'list-item', ml: 2 }}>
                <Typography>{t('pt.faq.a.5.1')}</Typography>
              </ListItem>
              <ListItem sx={{ display: 'list-item', ml: 2 }}>
                <Typography> {t('pt.faq.a.5.2')}</Typography>
              </ListItem>
              <ListItem sx={{ display: 'list-item', ml: 2 }}>
                <Typography>{t('pt.faq.a.5.3')}</Typography>
              </ListItem>
              <ListItem sx={{ display: 'list-item', ml: 2 }}>
                <Typography>{t('pt.faq.a.5.4')}</Typography>
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary aria-controls="panel1-content" id="panel1-header">
            <Typography>{t('pt.faq.q.6')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>{t('pt.faq.a.6')}</Typography>
          </AccordionDetails>
        </Accordion>

        <Typography textAlign="center" mt={4}>
          {currentLanguage === 'en'
            ? 'More questions? Please contact us at'
            : 'Noch Fragen? Bitte kontaktieren Sie uns unter'}{' '}
          <Link href="mailto:support.pt@ines-analytics.com">support.pt@ines-analytics.com</Link>{' '}
          {currentLanguage === 'en' ? 'for further queries.' : 'für weitere Fragen.'}
        </Typography>
      </Box>
    </>
  )
}

export default PtHome
